import type { InvestmentProjectItem } from '../types';
import { DynamicIcon } from './CommonComponents';
import { usePreviewTheme, withAlpha } from './PreviewTheme';

interface InvestmentProjectSectionProps {
  items: InvestmentProjectItem[];
}

// 截断文本工具
function TruncatedText({
  text,
  maxLines = 2,
  className = '',
  useRich = false,
}: {
  text: string;
  maxLines?: number;
  className?: string;
  useRich?: boolean;
}) {
  const style: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  if (useRich) {
    return (
      <div
        className={`rich-text-preview ${className}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }

  return (
    <div className={className} style={style} title={text}>
      {text}
    </div>
  );
}

export default function InvestmentProjectSection({ items }: InvestmentProjectSectionProps) {
  const theme = usePreviewTheme();

  if (!items || items.length === 0) {
    return null;
  }

  const handleJump = (link?: string) => {
    if (!link) return;
    if (/^https?:\/\//.test(link)) {
      window.open(link, '_blank');
    } else {
      window.location.href = link;
    }
  };

  return (
    <div>
      {/* 标题区域 */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold" style={{ color: theme.textHighlightColor }}>投资项目</h3>
          <p className="text-xs text-gray-400 tracking-widest ml-1">INVESTMENT PROJECTS</p>
        </div>
      </div>

      {/* 项目列表 */}
      <div className="p-4 space-y-3">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 p-3 rounded-xl hover:shadow-md transition-all duration-200 group"
            style={{
              border: `1px solid ${item.iconBgColor || theme.themeColor}20`,
            }}
          >
            {/* 左侧图标 */}
            <div
              className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${item.iconBgColor || theme.themeColor}20, ${item.iconBgColor || theme.themeColor}10)`,
              }}
            >
              <span style={{ color: item.iconBgColor || theme.themeColor }}>
                <DynamicIcon
                  iconName={item.icon}
                  className="w-6 h-6"
                />
              </span>
            </div>

            {/* 右侧内容 */}
            <div className="flex-1 min-w-0">
              {/* 标题 - 两行省略 */}
              <TruncatedText
                text={item.title}
                maxLines={2}
                className="text-sm font-semibold text-gray-800 mb-1"
              />

              {/* 描述 - 两行省略（富文本） */}
              <TruncatedText
                text={item.description}
                maxLines={2}
                className="text-[11px] text-gray-600 leading-relaxed mb-2 min-h-[30px]"
                useRich
              />

              {/* 查看全部按钮 */}
              <div className="flex justify-end">
                <button
                  onClick={() => handleJump(item.link)}
                  className="text-[11px] flex items-center gap-0.5 transition-colors"
                  style={{ color: theme.themeColor }}
                >
                  查看全部
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
